/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpErrorResponse } from "@angular/common/http";
import { MatDialogRef } from "@angular/material/dialog";
import moment from "moment";
import { environment } from "../../../environments/environment";
import { DialogComponent } from "../components/dialog/dialog.component";
import { CalculatorOptions } from "../models/http/calculator/calculator-options.model";
import { CloudProvider, PlatformType } from "../models/http/calculator/computing-platform.model";
import { StorageType } from "../models/http/calculator/storage-data.model";
import { ApplicationError } from "../models/ui/application-error.model";
import { CalculatorForm } from "../models/ui/calculator-form.model";
import { CioPeriod } from "../models/ui/cio-period.model";
import { DialogCloseReason } from "../models/ui/dialog-close-reason.model";
import { RequestParams } from "../models/ui/request-params.model";
import { SelectOption } from "../models/ui/select-option.model";
import { TimePeriodRange } from "../models/ui/time-period-range.model";
import { DialogService } from "../services/dialog.service";
import { MONTHS } from "./constants";

export const DATE_FORMAT_REGEXP = /^(0?[1-9]|[12][0-9]|3[01])\/(0?[1-9]|1[0-2])\/\d{4}$/;

/**
 * Converts a tab label to a route-friendly string.
 *
 * This function takes a tab label as input, converts it to lowercase,
 * and replaces all spaces with hyphens.
 *
 * @param label - The tab label to be converted.
 * @returns The converted route string.
 */
export function convertTabLabelToRoute(label: string): string {
	return label.toLowerCase().replace(/ /g, "-");
}

/**
 * Transforms a given number by rounding it to three decimal places.
 *
 * @param value - The number to be transformed.
 * @returns The transformed number rounded to three decimal places.
 */
export function roundValueUpTo(value: number, maximumFractionDigits: number = 3) {
	return truncateValueTo(value, maximumFractionDigits);
}

/**
 * Truncates a number to a maximum number of decimal digits.
 *
 * @param value - The number to truncate.
 * @param maximumFractionDigits - Maximum number of decimal digits to keep.
 * @returns The truncated number.
 */
export function truncateValueTo(value: number, maximumFractionDigits: number = 3) {
	if (!Number.isFinite(value)) return 0;

	const factor = Math.pow(10, maximumFractionDigits);
	return Math.trunc(value * factor) / factor;
}

/**
 * Formats a number with a maximum number of decimal digits.
 *
 * @param value - The number to format.
 * @param maximumFractionDigits - Maximum number of decimal digits to display.
 * @param useGrouping - Whether to use thousands separators.
 * @returns A formatted string or an empty string for invalid numbers.
 */
export function formatNumberMaxDecimals(
	value: number,
	maximumFractionDigits: number = 3,
	useGrouping: boolean = false
): string {
	if (!Number.isFinite(value)) return "";

	const truncatedValue = truncateValueTo(value, maximumFractionDigits);

	return new Intl.NumberFormat("en-US", {
		minimumFractionDigits: 0,
		maximumFractionDigits,
		useGrouping
	}).format(truncatedValue);
}

/**
 * Recursively overrides properties of an initial object with properties from an override object.
 * If a property in the override object is an object itself and not an array, the function will
 * recursively merge the properties of that object.
 *
 * @param initial - The initial object to be overridden.
 * @param override - The object containing properties to override in the initial object.
 * @returns The resulting object after applying the overrides.
 */
export function overrideObject(initial: any, override: any): any {
	for (const [key, value] of Object.entries(override)) {
		if (typeof value === "object" && !Array.isArray(value) && initial[key]) {
			initial[key] = overrideObject(initial[key], value);
		} else {
			initial[key] = value;
		}
	}

	return initial;
}

/**
 * Replaces path parameters in the given endpoint with their corresponding values from the provided pathParams object.
 *
 * @param endpoint - The endpoint string containing path parameters in the format `:paramName`.
 * @param pathParams - An optional object containing key-value pairs where the key is the parameter name and the value is the parameter value to replace in the endpoint.
 * @returns The endpoint string with path parameters replaced by their corresponding values from pathParams.
 */
export function getEndpointWithPathParams(endpoint: string, pathParams?: RequestParams): string {
	return endpoint.replace(/:(\w+)/g, (_, key) => String(pathParams?.[key] || ""));
}

/**
 * Returns a time period range representing the past year.
 *
 * The range starts from the current date minus one year and ends at the current date.
 *
 * @returns An object containing the start and end dates of the past year range.
 */
export function getLastYearRange(): TimePeriodRange {
	return {
		start: moment.utc().subtract(1, "year").startOf("day").toDate(),
		end: moment.utc().add(1, "day").startOf("day").toDate()
	};
}

/**
 * Returns a time period range representing the last week.
 *
 * @returns An object containing the start and end dates of the last week.
 */
export function getLastWeekRange(): TimePeriodRange {
	return {
		start: moment.utc().subtract(7, "days").startOf("day").toDate(),
		end: moment.utc().add(1, "day").startOf("day").toDate()
	};
}

/**
 * Maps a CIO period selection to its effective date range.
 * Fiscal Year runs from April 1 to March 31.
 */
export function getCioPeriodRange(period: CioPeriod): TimePeriodRange {
	const currentDate = moment.utc();

	switch (period) {
		case "CURR_Y":
			return {
				start: currentDate.clone().startOf("year").toDate(),
				end: currentDate.clone().add(1, "day").startOf("day").toDate()
			};
		case "CURR_Q":
			return {
				start: currentDate.clone().startOf("quarter").toDate(),
				end: currentDate.clone().add(1, "day").startOf("day").toDate()
			};
		case "CURR_S": {
			const month = currentDate.month();
			const isFirstSemester = month < 6;

			return {
				start: currentDate
					.clone()
					.month(isFirstSemester ? 0 : 6)
					.startOf("month")
					.toDate(),
				end: currentDate.clone().add(1, "day").startOf("day").toDate()
			};
		}
		case "LAST_Y":
			return {
				start: currentDate.clone().subtract(1, "year").startOf("year").toDate(),
				end: currentDate.clone().startOf("year").toDate()
			};
		case "LAST_Q":
			return {
				start: currentDate.clone().subtract(1, "quarter").startOf("quarter").toDate(),
				end: currentDate.clone().startOf("quarter").toDate()
			};
		case "LAST_S": {
			const month = currentDate.month();
			const isFirstSemester = month < 6;

			if (isFirstSemester) {
				return {
					start: currentDate
						.clone()
						.subtract(1, "year")
						.month(6)
						.startOf("month")
						.toDate(),
					end: currentDate.clone().startOf("year").toDate()
				};
			}

			return {
				start: currentDate.clone().month(0).startOf("month").toDate(),
				end: currentDate.clone().month(6).startOf("month").toDate()
			};
		}
		case "FY":
		default: {
			const fiscalYearStartYear =
				currentDate.month() >= 3 ? currentDate.year() : currentDate.year() - 1;

			return {
				start: currentDate
					.clone()
					.year(fiscalYearStartYear)
					.month(3)
					.startOf("month")
					.toDate(),
				end: currentDate
					.clone()
					.year(fiscalYearStartYear + 1)
					.month(3)
					.startOf("month")
					.toDate()
			};
		}
	}
}

/**
 * Generates an array of SelectOption objects based on the given core count.
 * Each SelectOption object will have a label and value corresponding to the core number.
 *
 * @param coreCount - The number of cores to generate options for.
 * @returns An array of SelectOption objects with labels and values from 1 to coreCount.
 */
export function generateCoreOptions(coreCount: number): SelectOption[] {
	return Array.from({ length: coreCount }, (_, i) => ({
		label: `${i + 1}`,
		value: `${i + 1}`
	}));
}

/**
 * Formats a Date object for sending to the backend API.
 * Returns the date in yyyy-MM-ddTHH:mm:ss format (UTC, no timezone suffix).
 *
 * @param date - The date to format.
 * @returns The formatted date string, or undefined if the input is null/undefined.
 */
export function formatDateForBE(date: Date | undefined | null): string | undefined {
	if (!date) return undefined;
	const pad = (n: number) => n.toString().padStart(2, "0");
	return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
}

/**
 * Generates calculator options based on the provided controls and whether the platform is on-premise.
 *
 * @param {boolean} isOnPremise - Indicates if the platform is on-premise.
 * @param {CalculatorForm} controls - The form controls containing the necessary data for the calculator options.
 * @returns The generated calculator options or undefined if controls are not provided.
 */
export function getCalculatorOptions(isOnPremise: boolean, controls: CalculatorForm) {
	const calculatorOptions: CalculatorOptions = {
		startDate: formatDateForBE(controls.timePeriod.start) ?? "",
		endDate: formatDateForBE(controls.timePeriod.end),
		cpuData: {
			model: controls.cpuModel,
			cores: controls.cpuCores,
			load: controls.cpuLoad,
			...(isOnPremise && {
				pue: controls.pue
			})
		},
		...(isOnPremise && {
			gpuData: {
				model: controls.gpuModel,
				number: controls.gpuCores,
				load: controls.gpuLoad
			}
		}),
		memoryData: {
			size: controls.memorySize
		},
		storageData: {
			type: controls.storageType as StorageType,
			size: controls.storageSize
		},
		computingPlatform: {
			platformType: controls.computingPlatformType as PlatformType,
			cloudProvider: (controls.cloudProvider as CloudProvider) || null,
			area: controls.computingPlatformAreas,
			region: controls.computingPlatformRegions
		}
	};

	return calculatorOptions;
}
/**
 * Calculates the index of an item in a paginated table and returns it as a zero-padded string.
 *
 * @param currentPage - The current page number (0-based index).
 * @param pageSize - The number of items per page.
 * @param index - The index of the item on the current page (optional, defaults to 0 if null or undefined).
 * @returns The zero-padded string representation of the item's index in the paginated table.
 */
export function getTableIndex(currentPage: number, pageSize: number, index?: number | null) {
	const paginatedIndex = currentPage * pageSize + (index || 0) + 1;
	return paginatedIndex.toString().padStart(3, "0");
}

/**
 * Creates a deep clone of the provided object.
 *
 * @template T - The type of the object to be cloned.
 * @param {T} obj - The object to be cloned. Can be of any type.
 * @returns {T} - A deep clone of the provided object.
 *
 * @remarks
 * - If the object is `null` or not of type "object", it returns the object itself.
 * - If the object is a function, it returns the function itself.
 * - If the object is an array, it recursively clones each element of the array.
 * - For plain objects, it recursively clones each property of the object.
 *
 * @example
 * ```typescript
 * const original = { a: 1, b: { c: 2 } };
 * const clone = deepClone(original);
 * console.log(clone); // { a: 1, b: { c: 2 } }
 * console.log(clone === original); // false
 * console.log(clone.b === original.b); // false
 * ```
 */
export function deepClone<T>(obj: T): T {
	if (obj === null || typeof obj !== "object") {
		return obj;
	}

	if (typeof obj === "function") {
		return obj;
	}

	if (Array.isArray(obj)) {
		return obj.map(deepClone) as unknown as T;
	}

	const clonedObj = {} as T;

	for (const key in obj) {
		if (Object.prototype.hasOwnProperty.call(obj, key)) {
			clonedObj[key] = deepClone(obj[key]);
		}
	}

	return clonedObj;
}

/**
 * Reverses the given list if the condition is true.
 *
 * @param list - The array to potentially reverse.
 * @param condition - A boolean value that determines whether to reverse the list.
 * @returns The original list if the condition is false, otherwise the reversed list.
 */
export function reverseConditionally<T>(list: T[], condition: boolean): T[] {
	return condition ? list.reverse() : list;
}

/**
 * Converts an ISO date string to a more human-readable format.
 *
 * @param date - The ISO date string to be converted.
 * @returns A string representing the date in the format "DD Month YYYY".
 */
export function prettifyISODate(date: string): string {
	const parsedDate = new Date(date);
	const day = parsedDate.getDate().toString().padStart(2, "0");
	const month = MONTHS[parsedDate.getMonth()];
	const year = parsedDate.getFullYear();
	const hours = parsedDate.getHours().toString().padStart(2, "0");

	return `${day} ${month} ${year} ${hours}:00`;
}

/**
 * Converts a base64 string to a Blob object with the specified MIME type.
 *
 * @param base64 - The base64 string to be converted to a Blob.
 * @param type - The MIME type of the Blob.
 * @returns A Blob object containing the data from the base64 string.
 */
export function mapBase64ToBlob(base64: string, type: string): Blob {
	const byteCharacters = atob(base64);
	const byteNumbers = new Array(byteCharacters.length);

	for (let i = 0; i < byteCharacters.length; i++) {
		byteNumbers[i] = byteCharacters.charCodeAt(i);
	}

	const byteArray = new Uint8Array(byteNumbers);

	return new Blob([byteArray], { type });
}

/**
 * Checks if a given value is either `null` or `undefined`.
 *
 * @param value - The value to check.
 * @returns `true` if the value is `null` or `undefined`, otherwise `false`.
 */
export function isNullOrUndefined(value: unknown): boolean {
	return value === null || value === undefined;
}

/**
 * Opens a dialog to inform the user that access to a resource is forbidden.
 *
 * @param dialogService - The service used to open the dialog.
 * The dialog will display an informational message with a warning icon,
 * indicating that the user does not have permission to access the resource.
 */
export function openForbiddenDialog(dialogService: DialogService) {
	dialogService.open({
		data: {
			type: "info",
			title: "Forbidden",
			message: "You do not have permission to access this resource.",
			iconName: "warning-hex",
			isIconVisible: true,
			closeBtnText: "Ok"
		}
	});
}

/**
 * Formats a given date using the Moment.js library by setting it to the start of the day.
 *
 * @param date - The date to format. Can be a `Date` object, a string, or `null`.
 * @returns A `Date` object representing the start of the day for the given date.
 *
 * @remarks
 * If the input is `null`, the behavior depends on how Moment.js handles `null` values.
 * Ensure that Moment.js is imported and available in the project for this function to work.
 */
export function formatDateWithMoment(date: Date | string | null, isEndDate: boolean = false): Date {
	const formattedDate = moment.utc(date).startOf("day").toDate();

	if (isEndDate) formattedDate.setUTCDate(formattedDate.getUTCDate() + 1);

	return formattedDate;
}

/**
 * Generates a time period range representing the current day.
 *
 * The range starts at the beginning of the day (00:00:00.000)
 * and ends at midnight of the following day (exclusive), in UTC.
 *
 * @returns {TimePeriodRange} An object containing the start and end
 * dates for the current day.
 */
export function todayAsRange(): TimePeriodRange {
	const start = new Date();
	start.setUTCHours(0, 0, 0, 0);

	const end = new Date(start);
	end.setUTCDate(end.getUTCDate() + 1);

	return { start, end };
}

/**
 * Logs an error to the console with detailed information.
 *
 * This function attempts to extract and log the stack trace, error message,
 * and additional application-specific error details (class name and method name)
 * if available. If no specific error details are found, it logs a generic
 * "Unknown error occurred" message.
 *
 * @param error - The error object to be logged. It can be of any type, but
 *                it is expected to conform to either the `Error` interface
 *                or the `ApplicationError` interface.
 *
 * @remarks
 * - If the `error` parameter is an instance of `Error`, its `stack` and `message`
 *   properties are used for logging.
 * - If the `error` parameter conforms to the `ApplicationError` interface, its
 *   `className`, `methodName`, and `message` properties are used for logging.
 * - If none of the above properties are available, a generic error message is logged.
 *
 * @example
 * ```typescript
 * try {
 *   throw new Error("Something went wrong");
 * } catch (err) {
 *   logError(err);
 * }
 * ```
 */
export function logError(error: unknown) {
	const simpleError = error as Error;
	const errorStack = simpleError.stack;
	const errorMessage = simpleError.message || "Unknown error occurred";
	const { message: applicationErrorMessage, className, methodName } = error as ApplicationError;

	console.error(
		`[ERROR HANDLER]${className && methodName ? ` - [${className}] - [${methodName}]` : ""}: ${errorStack || applicationErrorMessage || errorMessage}`
	);
}

/**
 * Determines whether an error dialog should be opened based on the provided error and dialog reference.
 *
 * @param error - The error object to evaluate. It can be of any type but is expected to conform to
 *                the `ApplicationError` or `HttpErrorResponse` structure.
 * @param dialogRef - A reference to the currently open dialog, if any. If `null`, no dialog is open.
 *
 * @returns `true` if an error dialog should be opened; otherwise, `false`.
 *          The dialog will not open if:
 *          - A dialog is already open (`dialogRef` is not `null`).
 *          - The error does not have the `shouldOpenErrorDialog` property set to `true`.
 *          - The error has an HTTP status of 401 (Unauthorized) or 403 (Forbidden).
 */
export function shouldOpenErrorDialog(
	error: unknown,
	dialogRef: MatDialogRef<DialogComponent, DialogCloseReason> | null
): boolean {
	return (
		!dialogRef &&
		(isApplicationErrorWithDialog(error) || !isUnauthorizedOrForbiddenError(error))
	);
}

/**
 * Determines if the provided error is an instance of `ApplicationError`
 * and is configured to open an error dialog.
 *
 * @param error - The error object to evaluate.
 * @returns `true` if the error is an instance of `ApplicationError` and
 *          has the `shouldOpenErrorDialog` property set to `true`, otherwise `false`.
 */
export function isApplicationErrorWithDialog(error: unknown): boolean {
	return error instanceof ApplicationError && error.shouldOpenErrorDialog;
}

/**
 * Checks if the provided error is an instance of `HttpErrorResponse` and has a status code
 * indicating an unauthorized (401) or forbidden (403) error.
 *
 * @param error - The error object to evaluate.
 * @returns `true` if the error is an `HttpErrorResponse` with a status of 401 or 403, otherwise `false`.
 */
export function isUnauthorizedOrForbiddenError(error: unknown): boolean {
	return error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403);
}

/**
 * Triggers the download of a file from a given URL.
 *
 * This function creates an anchor (`<a>`) element dynamically, sets its `href` attribute
 * to the provided URL, and sets the `download` attribute to the specified file name.
 * It then simulates a click on the anchor element to initiate the download and removes
 * the anchor element from the DOM afterward.
 *
 * @param document - The `Document` object used to create and manipulate the anchor element.
 * @param url - The URL of the file to be downloaded.
 * @param fileName - (Optional) The name to save the file as. Defaults to "image.png" if not provided.
 */
export function saveAs(document: Document, url: string, fileName?: string) {
	const anchor = document.createElement("a");

	anchor.href = url;
	anchor.download = fileName || "image.png";

	document.body.appendChild(anchor);

	anchor.click();

	document.body.removeChild(anchor);
}

/**
 * Constructs a full API URL by appending the given endpoint to the base API URL.
 * Optionally removes versioning from the base URL if not needed.
 *
 * @param endpoint - The API endpoint to append to the base URL.
 * @param isVersioningNeeded - Whether to include versioning in the base URL. Defaults to `true`.
 * @returns The constructed full API URL as a string.
 */
export function getUrl(endpoint: string, isVersioningNeeded: boolean = true): string {
	const apiUrlWithoutVersioning = environment.apiUrl.replace(/\/api\/v\d+$/, "");
	const url = isVersioningNeeded ? environment.apiUrl : apiUrlWithoutVersioning;

	return `${url}/${endpoint}`;
}
